
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Edit, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { GymClass } from '@/api/classes';
import { EditTimeSlotDialog } from './edit-time-slot-dialog';
import { BookingsDialog } from './bookings-dialog';

interface TimeSlotsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymClass: GymClass;
}

export function TimeSlotsDialog({
  open,
  onOpenChange,
  gymClass,
}: TimeSlotsDialogProps) {
  const { t } = useTranslation();
  const [editTimeSlot, setEditTimeSlot] = useState<{
    timeSlotId: string;
    data: any;
  } | null>(null);
  const [viewBookings, setViewBookings] = useState<string | null>(null);

  if (!gymClass.timeSlots || gymClass.timeSlots.length === 0) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('classes.timeSlots')}</DialogTitle>
            <DialogDescription>
              {gymClass.name} - {gymClass.label}
            </DialogDescription>
          </DialogHeader>
          <div className="text-center py-8 text-muted-foreground">
            {t('classes.noTimeSlots')}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('classes.timeSlots')}</DialogTitle>
            <DialogDescription>
              {gymClass.name} - {gymClass.label}
            </DialogDescription>
          </DialogHeader>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('classes.time')}</TableHead>
                <TableHead>{t('classes.schedule', 'Schedule')}</TableHead>
                <TableHead>{t('classes.instructor', 'Instructor')}</TableHead>
                <TableHead>{t('classes.maxParticipants')}</TableHead>
                <TableHead>{t('classes.bookedCount')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead className="text-right">
                  {t('common.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {gymClass.timeSlots.map((slot) => {
                const bookedCount = slot.bookedCount || 0;
                const availableSpots = slot.maxParticipants - bookedCount;

                // Helper to get day names
                const getDayNames = (days: number[]) => {
                  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                  return days.map((d) => names[d]).join(', ');
                };

                return (
                  <TableRow key={slot.timeSlotId || slot._id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {slot.startTime} - {slot.endTime}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {slot.date ? (
                        <div>
                          <Badge variant="secondary">
                            {t('classes.specificDate', 'Specific Date')}
                          </Badge>
                          <div className="text-xs text-muted-foreground mt-1">
                            {slot.date}
                          </div>
                        </div>
                      ) : slot.daysOfWeek && slot.daysOfWeek.length > 0 ? (
                        <div>
                          <Badge variant="outline">
                            {t('classes.daysOfWeek', 'Days of Week')}
                          </Badge>
                          <div className="text-xs text-muted-foreground mt-1">
                            {getDayNames(slot.daysOfWeek)}
                          </div>
                        </div>
                      ) : slot.applyEveryDay ? (
                        <Badge variant="default">
                          {t('classes.everyDay', 'Every Day')}
                        </Badge>
                      ) : (
                        <Badge variant="outline">
                          {t('classes.notSet', 'Not Set')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {slot.staffMember ? (
                        <div>
                          <div className="font-medium text-sm">
                            {slot.staffMember.name} {slot.staffMember.surname}
                          </div>
                          <Badge variant="outline" className="mt-1 text-xs">
                            {slot.staffMember.userType}
                          </Badge>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {t('classes.noInstructor', 'No instructor')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{slot.maxParticipants}</TableCell>
                    <TableCell>
                      {bookedCount} / {slot.maxParticipants}
                      <span className="text-muted-foreground ml-2">
                        ({availableSpots} {t('classes.availableSpots')})
                      </span>
                    </TableCell>
                    <TableCell>
                      {slot.disabled ? (
                        <Badge variant="secondary">
                          {t('classes.disabled')}
                        </Badge>
                      ) : slot.isFull ? (
                        <Badge variant="destructive">
                          {t('classes.isFull')}
                        </Badge>
                      ) : (
                        <Badge variant="default">{t('classes.active')}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setEditTimeSlot({
                            timeSlotId: (slot.timeSlotId || slot._id)!,
                            data: slot,
                          })
                        }
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setViewBookings((slot.timeSlotId || slot._id)!)
                        }
                      >
                        <Users className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {editTimeSlot && (
        <EditTimeSlotDialog
          open={!!editTimeSlot}
          onOpenChange={(open: boolean) => !open && setEditTimeSlot(null)}
          gymClass={gymClass}
          timeSlotId={editTimeSlot.timeSlotId}
          timeSlot={editTimeSlot.data}
        />
      )}

      {viewBookings && (
        <BookingsDialog
          open={!!viewBookings}
          onOpenChange={(open: boolean) => !open && setViewBookings(null)}
          gymClass={gymClass}
          timeSlotId={viewBookings}
        />
      )}
    </>
  );
}
